import { Box, ButtonBase, Typography } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { memo, type KeyboardEvent, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type {
    ClinicalPeopleList,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import type { OpenInViewer } from '~/hooks/openInViewer';
import {
    AssigneePicker,
    CourseStageSummary,
    ImportActivityChip,
    OpenButtonHint,
    PATIENT_META_LINE_SX,
    PatientIdentity,
    PresenceChip,
    PatientCare,
    TreatmentStart,
} from '../shared';
import ModalityChip from '~/components/Chip/ModalityChip';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    formatDayText,
    patientAssignee,
    patientImageModalities,
    patientImportActivity,
    patientCourseStagesDone,
    patientRtObjects,
    resolveCourseStage,
    type ClinicalLists,
} from '../model';
import {
    PatientTableRow,
    visiblePatientListColumns,
    PatientListBodyCell,
    type PatientListColumnId,
} from './PatientListTable';

type PatientRowProps = {
    patient: PatientSummaryDto;
    compact: boolean;
    selected: boolean;
    onSelect: (id: string) => void;
    onOpenInViewer: OpenInViewer;
    /** True while another open is in flight; the open control is disabled. */
    openBusy: boolean;
    lists: ClinicalLists;
    people: ClinicalPeopleList | undefined;
    /** Whether the user may assign the patient to anybody, from the row. */
    canAssign: boolean;
    /** Today, injected so the age and the treatment start warning are testable. */
    today: Date;
};

const PatientRow = memo(function PatientRow({
    patient,
    compact,
    selected,
    onSelect,
    onOpenInViewer,
    openBusy,
    lists,
    people,
    canAssign,
    today,
}: PatientRowProps) {
    const intl = useIntl();
    const stage = resolveCourseStage(patient.courseStage);
    const stagesDone = patientCourseStagesDone(patient.courseStagesDone);
    const assignee = patientAssignee(intl, people, patient);
    const caseNames = (patient.caseNames ?? []).join(' · ');
    const modalities = patientImageModalities(patient);
    const rtObjects = patientRtObjects(patient);
    const lastActivity = formatDayText(intl, patient.lastActivityAt);
    // Rows without a backend id cannot open (or close) the panel.
    const patientId = patient.id;
    const onOpen =
        patientId !== undefined ? () => onSelect(patientId) : undefined;
    // The hover icon opens the patient straight in the viewer, on its work
    // case where it has one, bypassing the panel; the row body still expands
    // the drilldown.
    const openThisPatient =
        patientId !== undefined && !openBusy
            ? () => onOpenInViewer(patientId, 'all', { workCase: true })
            : undefined;

    const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.currentTarget !== event.target) {
            return;
        }
        if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onOpen();
        }
    };

    const presence = (
        <PresenceChip
            activeEditorCount={patient.activeEditorCount}
            activeEditingCount={patient.activeEditingCount}
        />
    );

    const datasetChips = (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
            }}
        >
            {modalities.map(([modality, count]) => (
                <ModalityChip
                    key={modality}
                    modality={modality}
                    count={count}
                />
            ))}
            {rtObjects.map((label) => (
                <ModalityChip key={label} modality={label} />
            ))}
            <ImportActivityChip {...patientImportActivity(patient)} />
        </Box>
    );

    // One cell per column id: the Record makes a missing or extra cell a
    // compile error, and PATIENT_LIST_COLUMNS order keeps body and header aligned.
    const cells: Record<PatientListColumnId, ReactNode> = {
        patient: (
            <PatientIdentity patient={patient} today={today} tags={lists.tags}>
                {/* The patient's cases, by name. */}
                {caseNames && (
                    <Box
                        sx={{
                            ...PATIENT_META_LINE_SX,
                            fontFamily: 'inherit',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            component="span"
                            noWrap
                            fontSize={13}
                            color="text.secondary"
                        >
                            <SensitiveInfoTypography>
                                {caseNames}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>
                )}
                {/* Only with something to fold in: an empty line still takes
                    the column's gap and grows the row as the panel opens. */}
                {compact && assignee && (
                    <Box
                        sx={{
                            ...PATIENT_META_LINE_SX,
                            fontFamily: 'inherit',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography component="span" noWrap fontSize={13}>
                            {assignee.name}
                        </Typography>
                    </Box>
                )}
            </PatientIdentity>
        ),
        workflow: compact ? (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {presence && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            minWidth: 0,
                        }}
                    >
                        {presence}
                    </Box>
                )}
                <CourseStageSummary stage={stage} done={stagesDone} />
            </Box>
        ) : (
            <CourseStageSummary stage={stage} done={stagesDone} />
        ),
        care: canAssign ? (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    minWidth: 0,
                }}
            >
                <PatientCare patient={patient} lists={lists} people={people} />
                <AssigneePicker patient={patient} people={people} />
            </Box>
        ) : (
            <PatientCare
                patient={patient}
                lists={lists}
                people={people}
                assignee={assignee}
            />
        ),
        treatmentStart: <TreatmentStart patient={patient} today={today} />,
        datasets: datasetChips,
        lastActivity: (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '3px',
                    }}
                >
                    <Typography
                        variant="monoMeta"
                        sx={{ fontSize: 12, color: 'text.primary' }}
                    >
                        {lastActivity}
                    </Typography>
                    {presence}
                </Box>
                <Box
                    className="patient-list-open"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 2,
                        pl: 6,
                        // The stop must be opaque to occlude the cell text;
                        // the neutral lift matches an unselected hovered row.
                        background: (theme) =>
                            `linear-gradient(to right, transparent, ${theme.palette.action.hoverNeutral} 42%)`,
                        opacity: 0,
                        transition: 'opacity 120ms',
                        pointerEvents: 'none',
                    }}
                >
                    <OpenButtonHint
                        enabled={!!openThisPatient}
                        title={intl.formatMessage({
                            id: openBusy
                                ? 'patientList.open.inFlight'
                                : 'patientList.openHint',
                        })}
                    >
                        <ButtonBase
                            disabled={!openThisPatient}
                            aria-label={intl.formatMessage({
                                id: 'patientList.row.openPatient',
                            })}
                            onClick={(event) => {
                                event.stopPropagation();
                                openThisPatient?.();
                            }}
                            sx={{
                                display: 'inline-flex',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                color: 'text.secondary',
                                '&.Mui-disabled': {
                                    color: 'text.secondary',
                                },
                            }}
                        >
                            <ExternalLink size={16} />
                        </ButtonBase>
                    </OpenButtonHint>
                </Box>
            </>
        ),
    };

    return (
        <PatientTableRow
            isSelected={selected}
            tabIndex={onOpen ? 0 : undefined}
            aria-expanded={onOpen ? selected : undefined}
            onClick={onOpen}
            onKeyDown={onKeyDown}
        >
            {visiblePatientListColumns(compact).map((column) => (
                <PatientListBodyCell key={column.id} alignEnd={column.alignEnd}>
                    {cells[column.id]}
                </PatientListBodyCell>
            ))}
        </PatientTableRow>
    );
});

export default PatientRow;
